<template>
  <v-card class="clients-card" elevation="1" outlined>
    <!-- Header -->
    <v-card-title class="clients-header d-flex flex-wrap align-center py-4 px-5">
      <div class="d-flex align-center">
        <v-avatar color="primary lighten-5" size="40" class="me-3">
          <v-icon color="primary" size="22">mdi-account-group</v-icon>
        </v-avatar>
        <div>
          <h2 class="text-h6 font-weight-bold text--primary mb-0">My Top‑Up Clients</h2>
          <p class="text-caption grey--text mb-0 mt-1">{{ clients.length }} client{{ clients.length !== 1 ? 's' : '' }}</p>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        label="Search clients..."
        single-line
        hide-details
        dense
        outlined
        rounded
        clearable
        class="clients-search"
      ></v-text-field>
    </v-card-title>

    <v-divider></v-divider>

    <!-- Table -->
    <v-data-table
      :headers="headers"
      :items="clients"
      item-key="downline_id"
      :items-per-page="10"
      :search="search"
      :loading="loading"
      loading-text="Loading clients..."
      no-data-text="No clients found"
      class="clients-table elevation-0"
      hide-default-footer
      :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
    >
      <!-- LOADING SKELETON -->
      <template v-slot:loading>
        <v-skeleton-loader
          v-for="n in 5"
          :key="n"
          type="list-item-avatar-two-line"
          class="mx-4 my-2"
        ></v-skeleton-loader>
      </template>

      <!-- CLIENT NAME + INFO -->
      <template #[`item.full_name`]="{ item }">
        <div class="d-flex align-center py-2">
          <v-avatar size="40" :color="item.avatar ? '' : 'primary lighten-4'" class="me-3">
            <v-img v-if="item.avatar" :src="item.avatar" alt="avatar"></v-img>
            <span v-else class="primary--text font-weight-bold text-subtitle-2">
              {{ getInitials(item.full_name) }}
            </span>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-bold text-body-2 text--primary text-truncate" style="max-width: 220px;">
              {{ item.full_name }}
            </span>
            <div class="d-flex align-center mt-1">
              <v-icon size="12" color="grey lighten-1" class="me-1">mdi-phone</v-icon>
              <span class="text-caption grey--text">{{ item.phone_number || '—' }}</span>
            </div>
            <div v-if="item.last_message" class="text-caption grey--text text--lighten-1 mt-1 line-clamp-1" style="max-width: 280px;">
              {{ item.last_message }}
            </div>
          </div>
        </div>
      </template>

      <!-- STATUS -->
      <template #[`item.unread`]="{ item }">
        <v-chip
          small
          :color="item.unread_count > 0 ? 'error' : 'success'"
          :text-color="item.unread_count > 0 ? 'white' : 'white'"
          class="font-weight-bold text-uppercase"
          label
        >
          <v-icon left x-small>
            {{ item.unread_count > 0 ? 'mdi-bell-alert' : 'mdi-check-circle' }}
          </v-icon>
          {{ item.unread_count > 0 ? `${item.unread_count} Unread` : 'Read' }}
        </v-chip>
      </template>

      <!-- ACTIONS -->
      <template #[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                small
                color="green darken-1"
                class="action-btn mx-1"
                @click="openWhatsApp(item)"
              >
                <v-icon small>mdi-whatsapp</v-icon>
              </v-btn>
            </template>
            <span>WhatsApp</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                small
                color="primary"
                class="action-btn mx-1"
                @click="openChat(item)"
              >
                <v-icon small>mdi-message-text</v-icon>
              </v-btn>
            </template>
            <span>Open Chat</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <!-- EMPTY STATE (custom, when no clients at all) -->
    <div v-if="!loading && clients.length === 0" class="d-flex flex-column align-center justify-center py-12 empty-state">
      <v-avatar size="80" color="grey lighten-4" class="mb-4">
        <v-icon size="40" color="grey lighten-1">mdi-account-off</v-icon>
      </v-avatar>
      <p class="text-subtitle-1 grey--text text--darken-1 font-weight-medium">No clients yet</p>
      <p class="text-caption grey--text text-center px-4">Your downline clients will appear here once they are registered.</p>
    </div>
  </v-card>
</template>

<script>
import axios from 'axios'

export default {
  name: 'TopUpClientsTable',
  data() {
    return {
      headers: [
        { text: 'Client', value: 'full_name', sortable: true },
        { text: 'Status', value: 'unread', sortable: true, align: 'center' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '140px' },
      ],
      clients: [],
      search: '',
      loading: false,
    }
  },
  mounted() {
    this.fetchDownlines()
  },
  methods: {
    async fetchDownlines() {
      this.loading = true
      try {
        const res = await axios.get('/api/v1/topup/my_downlines/')
        this.clients = (res.data.downlines || []).map(d => ({
          downline_id: d.downline_id,
          full_name: d.full_name,
          avatar: d.avatar,
          phone_number: d.phone_number || '',
          unread_count: d.unread_count || 0,
          last_message: d.last_message,
        }))
      } catch (error) {
        console.error('Failed to load clients:', error)
        this.$store.commit('setSnackBarMessage', 'Failed to load clients')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.loading = false
      }
    },

    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    },

    openWhatsApp(client) {
      const phone = this.formatPhone(client.phone_number)
      if (!phone) {
        this.$store.commit('setSnackBarMessage', 'No phone number available')
        this.$store.commit('setSnackBarColor', 'orange darken-2')
        this.$store.commit('activateSnackBar', true)
        return
      }
      const msg = `Hello ${client.full_name}, I'm your PettyCash agent. How can I help you?`
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank')
    },

    openChat(client) {
      this.$router.push({ name: 'topup-chat', query: { client: client.downline_id } })
    },

    formatPhone(raw) {
      if (!raw) return ''
      let digits = raw.replace(/\D/g, '')
      if (digits.startsWith('0')) digits = digits.substring(1)
      if (digits.length === 9) digits = '237' + digits
      return digits ? `+${digits}` : ''
    },
  },
}
</script>

<style scoped>
.clients-card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.clients-header {
  background: linear-gradient(to right, #ffffff, #fafafa);
}

.clients-search {
  max-width: 260px;
}

.clients-table ::v-deep th {
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666 !important;
  background: #fafafa !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
  height: 48px;
}

.clients-table ::v-deep td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
  vertical-align: middle;
}

.clients-table ::v-deep tr:hover {
  background: #f8f9fa !important;
}

.clients-table ::v-deep .v-data-table__empty-wrapper {
  display: none;
}

.action-btn {
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.15);
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-state {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

@media (max-width: 600px) {
  .clients-search {
    max-width: 100%;
    margin-top: 12px;
  }
  .clients-header {
    flex-direction: column !important;
    align-items: flex-start !important;
  }
}
</style>
